import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { deleteField, doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { isAdminRole } from '../../lib/rolePermissions';
import {
  formatMonthKeyLabel,
  formatUsd,
  listForwardMonthKeys,
  normalizeOptionalBudgetUsd,
  readAiSpendBudgetsFromSettings,
  resolveMonthBudgetUsd,
  utcMonthKey,
} from '../../lib/aiUsageSpend';

/**
 * @param {string} draft
 * @returns {{ ok: true, value: number | null } | { ok: false, error: string }}
 */
function parseBudgetDraft(draft) {
  const trimmed = typeof draft === 'string' ? draft.trim() : '';
  if (trimmed === '') return { ok: true, value: null };
  const next = normalizeOptionalBudgetUsd(Number(trimmed));
  if (next == null) {
    return {
      ok: false,
      error: 'Enter a positive dollar amount, or leave blank for none.',
    };
  }
  return { ok: true, value: next };
}

/**
 * @param {number | null} value
 * @returns {string}
 */
function draftFromBudget(value) {
  return value == null ? '' : String(value);
}

/**
 * Workspace AI Spend budgets: daily / weekly / monthly + 12-month plan.
 * Writes settings.aiSpendBudgets and keeps legacy monthlyAiBudgetUsd synced
 * to the current month's effective budget (alerting still reads that scalar).
 *
 * @param {{ companyId?: string | null, userRole?: string | null }} props
 */
export default function AiSpendBudgetSettings({ companyId, userRole }) {
  const resolvedCompanyId =
    typeof companyId === 'string' ? companyId.trim() : '';
  const allowed = Boolean(resolvedCompanyId && isAdminRole(userRole));

  const monthKeys = useMemo(() => listForwardMonthKeys(new Date(), 12), []);
  const currentMonthKey = useMemo(() => utcMonthKey(), []);

  const [dailyDraft, setDailyDraft] = useState('');
  const [weeklyDraft, setWeeklyDraft] = useState('');
  const [monthlyDraft, setMonthlyDraft] = useState('');
  const [capCallsDraft, setCapCallsDraft] = useState('');
  const [capSpendDraft, setCapSpendDraft] = useState('');
  const [byMonthDrafts, setByMonthDrafts] = useState(() => {
    /** @type {Record<string, string>} */
    const initial = {};
    monthKeys.forEach((key) => {
      initial[key] = '';
    });
    return initial;
  });
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const [loading, setLoading] = useState(allowed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const applyLoadedBudgets = useCallback(
    (budgets, spendCap) => {
      setDailyDraft(draftFromBudget(budgets.dailyUsd));
      setWeeklyDraft(draftFromBudget(budgets.weeklyUsd));
      setMonthlyDraft(draftFromBudget(budgets.monthlyUsd));
      /** @type {Record<string, string>} */
      const monthDrafts = {};
      monthKeys.forEach((key) => {
        monthDrafts[key] = draftFromBudget(
          Object.prototype.hasOwnProperty.call(budgets.byMonth, key)
            ? budgets.byMonth[key]
            : null,
        );
      });
      setByMonthDrafts(monthDrafts);
      setSavedSnapshot(budgets);
      const cap =
        spendCap && typeof spendCap === 'object' ? spendCap : {};
      setCapCallsDraft(
        cap.maxCallCount != null && Number(cap.maxCallCount) > 0
          ? String(cap.maxCallCount)
          : '',
      );
      setCapSpendDraft(
        cap.maxSpendUsd != null && Number(cap.maxSpendUsd) > 0
          ? String(cap.maxSpendUsd)
          : '',
      );
    },
    [monthKeys],
  );

  const load = useCallback(async () => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const snap = await getDoc(doc(db, 'companies', resolvedCompanyId));
      const data = snap.exists() ? snap.data() || {} : {};
      const settings =
        data.settings && typeof data.settings === 'object' ? data.settings : {};
      applyLoadedBudgets(
        readAiSpendBudgetsFromSettings(settings),
        settings.aiSpendCap,
      );
    } catch (err) {
      console.error('AiSpendBudgetSettings load:', err);
      setError(err?.message || 'Could not load AI Spend settings.');
    } finally {
      setLoading(false);
    }
  }, [allowed, applyLoadedBudgets, resolvedCompanyId]);

  useEffect(() => {
    load();
  }, [load]);

  const currentMonthSavedUsd = useMemo(() => {
    if (!savedSnapshot) return null;
    return resolveMonthBudgetUsd(savedSnapshot, currentMonthKey);
  }, [savedSnapshot, currentMonthKey]);

  const handleSave = async () => {
    if (!allowed) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const daily = parseBudgetDraft(dailyDraft);
      if (!daily.ok) {
        setError(`Daily budget: ${daily.error}`);
        setSaving(false);
        return;
      }
      const weekly = parseBudgetDraft(weeklyDraft);
      if (!weekly.ok) {
        setError(`Weekly budget: ${weekly.error}`);
        setSaving(false);
        return;
      }
      const monthly = parseBudgetDraft(monthlyDraft);
      if (!monthly.ok) {
        setError(`Monthly budget: ${monthly.error}`);
        setSaving(false);
        return;
      }

      /** @type {Record<string, number>} */
      const byMonth = {};
      for (const key of monthKeys) {
        const parsed = parseBudgetDraft(byMonthDrafts[key] || '');
        if (!parsed.ok) {
          setError(`${formatMonthKeyLabel(key)}: ${parsed.error}`);
          setSaving(false);
          return;
        }
        if (parsed.value != null) {
          byMonth[key] = parsed.value;
        }
      }

      const nextBudgets = {
        dailyUsd: daily.value,
        weeklyUsd: weekly.value,
        monthlyUsd: monthly.value,
        byMonth,
      };
      const legacyCurrent = resolveMonthBudgetUsd(nextBudgets, currentMonthKey);

      const capCalls = parseBudgetDraft(capCallsDraft);
      if (!capCalls.ok) {
        setError(`Default max calls: ${capCalls.error}`);
        setSaving(false);
        return;
      }
      const capSpend = parseBudgetDraft(capSpendDraft);
      if (!capSpend.ok) {
        setError(`Default max spend: ${capSpend.error}`);
        setSaving(false);
        return;
      }
      // Cap fields are positive integers/dollars; blank clears workspace default.
      const nextCap =
        capCalls.value == null && capSpend.value == null
          ? null
          : {
              ...(capCalls.value != null
                ? { maxCallCount: Math.floor(capCalls.value) }
                : {}),
              ...(capSpend.value != null ? { maxSpendUsd: capSpend.value } : {}),
            };
      if (
        nextCap &&
        nextCap.maxCallCount == null &&
        nextCap.maxSpendUsd == null
      ) {
        setError('Enter at least one of default max calls or max spend.');
        setSaving(false);
        return;
      }

      /** @type {Record<string, unknown>} */
      const patch = {
        'settings.aiSpendBudgets': nextBudgets,
        // Legacy scalar — Cloud Function alerting still reads this only.
        'settings.monthlyAiBudgetUsd': legacyCurrent,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      };
      if (nextCap == null) {
        patch['settings.aiSpendCap'] = deleteField();
      } else {
        patch['settings.aiSpendCap'] = nextCap;
      }

      await updateDoc(doc(db, 'companies', resolvedCompanyId), patch);

      applyLoadedBudgets(nextBudgets, nextCap);
      setMessage(
        legacyCurrent == null
          ? 'AI Spend budgets saved. Current-month budget cleared (no monthly alerts).'
          : `AI Spend budgets saved. Current-month budget synced to ${formatUsd(legacyCurrent)}.`,
      );
    } catch (err) {
      console.error('AiSpendBudgetSettings save:', err);
      setError(err?.message || 'Could not save AI Spend budgets.');
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) return null;

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Spend alerts (80% / 100%) still use the legacy monthly budget only.
        Daily, weekly, and future-month values are stored for planning and are
        not yet enforced by Cloud Functions.
      </Alert>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <SavingsOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                AI Spend
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optional USD budgets for this workspace. Estimates use logged
                token costs, not provider remaining balance.
              </Typography>
            </Box>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          ) : null}
          {message ? (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setMessage('')}
            >
              {message}
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  maxWidth: 520,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Current month saved budget
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {currentMonthSavedUsd == null
                    ? 'None (alerts off)'
                    : formatUsd(currentMonthSavedUsd)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatMonthKeyLabel(currentMonthKey)} · synced to{' '}
                  <code>monthlyAiBudgetUsd</code> for alerting
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Period budgets
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Monthly is the default when a month below has no override.
                  Leave a field blank for none.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ maxWidth: 720 }}
                >
                  <TextField
                    label="Daily budget (USD)"
                    type="number"
                    value={dailyDraft}
                    onChange={(e) => setDailyDraft(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    fullWidth
                  />
                  <TextField
                    label="Weekly budget (USD)"
                    type="number"
                    value={weeklyDraft}
                    onChange={(e) => setWeeklyDraft(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    fullWidth
                  />
                  <TextField
                    label="Monthly budget (USD)"
                    type="number"
                    value={monthlyDraft}
                    onChange={(e) => setMonthlyDraft(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Default for months without an override"
                    fullWidth
                  />
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Default per-ticket AI ceiling
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Workspace default hard stop for each ticket (max model calls
                  and spend). Per-ticket overrides and project defaults still
                  win when set. Leave blank to use the platform floor (25 calls
                  / $3.00).
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ maxWidth: 520 }}
                >
                  <TextField
                    label="Default max calls"
                    type="number"
                    value={capCallsDraft}
                    onChange={(e) => setCapCallsDraft(e.target.value)}
                    inputProps={{ min: 1, step: '1' }}
                    helperText="Blank = platform default"
                    fullWidth
                  />
                  <TextField
                    label="Default max spend (USD)"
                    type="number"
                    value={capSpendDraft}
                    onChange={(e) => setCapSpendDraft(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Blank = platform default"
                    fullWidth
                  />
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Forward monthly plan
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Current month plus the next 11. Override the monthly default
                  for a specific month, or leave blank to inherit.
                </Typography>
                <TableContainer sx={{ maxWidth: 640 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell align="right">Budget (USD)</TableCell>
                        <TableCell>Effective</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthKeys.map((key) => {
                        const draft = byMonthDrafts[key] || '';
                        const parsed = parseBudgetDraft(draft);
                        const override =
                          parsed.ok && parsed.value != null
                            ? parsed.value
                            : null;
                        const monthlyParsed = parseBudgetDraft(monthlyDraft);
                        const monthlyFallback =
                          monthlyParsed.ok ? monthlyParsed.value : null;
                        const effective =
                          override != null ? override : monthlyFallback;
                        const isCurrent = key === currentMonthKey;
                        return (
                          <TableRow
                            key={key}
                            selected={isCurrent}
                            hover
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: isCurrent ? 700 : 400 }}>
                                {formatMonthKeyLabel(key)}
                                {isCurrent ? ' (current)' : ''}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ width: 180 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={draft}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setByMonthDrafts((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                }}
                                inputProps={{ min: 0, step: '0.01' }}
                                placeholder="Inherit"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {effective == null
                                  ? 'None'
                                  : `${formatUsd(effective)}${
                                      override == null ? ' (default)' : ''
                                    }`}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save AI Spend budgets
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
